<script lang="ts" setup>
import {
  DialogContent,
  DialogOverlay,
  DialogTitle,
  DialogPortal,
  DialogRoot,
  DialogClose,
  VisuallyHidden,
} from 'reka-ui';

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  fullscreen: {
    type: Boolean,
    default: false,
  },
  hideClose: {
    type: Boolean,
    default: false,
  },
  transparentOverlay: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
});

const loading = ref(true);
const isModalOpened = ref(false);

const resetScrollLock = () => {
  if (!import.meta.client) {
    return;
  }

  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
};

const handleClose = () => {
  emit('close');
};

const emit = defineEmits(['close']);

watch(
  () => props.isVisible,
  (isVisible) => {
    isModalOpened.value = isVisible;
  },
  { immediate: true },
);

watch(isModalOpened, (open) => {
  if (!open) {
    emit('close');
    loading.value = true;
    resetScrollLock();
  }
});

watch(
  () => props.isVisible,
  async (isVisible) => {
    if (isVisible) {
      await nextTick();
      return;
    }

    resetScrollLock();
  },
);

onBeforeUnmount(() => {
  resetScrollLock();
});
</script>

<template>
  <DialogRoot v-model:open="isModalOpened">
    <DialogPortal>
      <DialogOverlay
        :class="[
          $style.overlay,
          { [$style.overlay_transparent!]: props.transparentOverlay },
        ]"
      />
      <DialogContent :aria-describedby="undefined">
        <VisuallyHidden as-child>
          <DialogTitle :class="$style.title">
            {{ props.title }}
          </DialogTitle>
        </VisuallyHidden>
        <div
          :class="[
            $style.content,
            { [$style.content_fullscreen!]: props.fullscreen },
          ]"
        >
          <slot />
        </div>

        <DialogClose
          v-if="!props.hideClose"
          :class="$style.close"
          aria-label="Close"
        >
          <Button view="icon" @click="handleClose">
            <Icon name="my-icon:i-close-white" />
          </Button>
        </DialogClose>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<style lang="scss" module>
/*
 * Above the ad blocks: AdFox writes `z-index: 100` into the style attribute of
 * a banner it fills, so anything below that draws under the top banner — the
 * heading of a modal centred on a tall screen lands right behind it
 */
.overlay {
  background-color: rgba(19, 0, 2, 0.7);
  position: fixed;
  inset: 0;
  z-index: 110;
  cursor: pointer;

  &_transparent {
    background-color: transparent;
  }
}

.content {
  position: fixed;
  top: 5vh;
  bottom: 5vh;
  margin: auto;
  left: 50%;
  transform: translate(-50%, 0);
  max-width: 1380px;
  max-height: 90vh;
  z-index: 115;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &_fullscreen {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100vw;
    max-width: 100vw;
    max-height: 100vh;
    transform: none;
    margin: 0;
    justify-content: stretch;
  }
}

.content:focus {
  outline: none;
}

.close {
  position: fixed;
  top: 0;
  right: 0;
  width: 88px;
  height: 88px;
  z-index: 115;
}

@media (max-width: 1500px) {
  .content {
    width: 90vw;
    max-width: 1120px;
  }
}

@media (max-width: 1379px) {
  .content {
    max-width: 1024px;
  }
}

@media (max-width: 600px) {
  .overlay {
    background-color: #130002;
    aspect-ratio: inherit;
  }

  .content {
    width: 100%;
    max-width: 100%;
    max-height: 100%;
  }
}
</style>
